import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Send, Store, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/components/Avatar";
import { api, errorMessage } from "@/lib/api";

function Comment({ c, onDelete }) {
  return (
    <div data-testid={`comment-${c.id}`} className="flex gap-2 items-start">
      <Avatar url={c.author_avatar} name={c.author_name} size="h-6 w-6" />
      <div className="flex-1 min-w-0 rounded-xl bg-[#0B0F19] border border-[#26334D] px-3 py-2">
        <div className="flex items-center gap-2 text-xs">
          {c.is_business ? <span className="font-semibold text-[#FFB800] flex items-center gap-1"><Store className="h-3 w-3" />{c.author_name} · Business</span>
            : <Link to={`/traveler/${c.author_id}`} className="font-semibold hover:text-[#FFB800]">{c.author_name}</Link>}
          {c.is_author && <span className="text-[10px] uppercase tracking-wider text-[#00E5A3]">Reviewer</span>}
          <span className="text-slate-500 ml-auto">{new Date(c.created_at).toLocaleDateString()}</span>
          {c.can_delete && <button data-testid={`comment-delete-${c.id}`} onClick={() => onDelete(c.id)} aria-label="Delete comment"><Trash2 className="h-3 w-3 text-[#FF7A7A]" /></button>}
        </div>
        <p className="text-sm text-slate-200 mt-0.5 break-words">{c.text}</p>
      </div>
    </div>
  );
}

export function CommentThread({ kind, target, count = 0, defaultOpen = false, canComment = true, tid = target }) {
  const [open, setOpen] = useState(defaultOpen);
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/comments", { params: { kind, target } }).then((r) => setItems(r.data)).catch(() => setItems([])), [kind, target]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/comments", { kind, target, text }); setItems(data); setText(""); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/comments/${id}`); setItems((xs) => xs.filter((c) => c.id !== id)); } catch (err) { toast.error(errorMessage(err)); }
  };
  const n = items ? items.length : count;

  return (
    <div data-testid={`thread-${tid}`} className="mt-2">
      <button type="button" data-testid={`thread-toggle-${tid}`} onClick={() => setOpen(!open)}
        className="text-xs text-slate-400 hover:text-[#00E5A3] flex items-center gap-1 transition-colors">
        <MessageCircle className="h-3.5 w-3.5" /> {open ? "Hide replies" : n ? `${n} ${n === 1 ? "reply" : "replies"}` : "Reply"}
      </button>
      {open && (
        <div className="mt-2 space-y-2 pl-2 border-l border-[#26334D]">
          {items?.map((c) => <Comment key={c.id} c={c} onDelete={remove} />)}
          {items?.length === 0 && <p className="text-xs text-slate-500">No replies yet. Start the conversation.</p>}
          {canComment ? (
            <form onSubmit={send} className="flex gap-2">
              <input data-testid={`thread-input-${tid}`} value={text} onChange={(e) => setText(e.target.value)} maxLength={600} placeholder="Write a public reply…"
                className="flex-1 min-w-0 bg-[#0B0F19] border border-[#26334D] rounded-full px-3 py-1.5 text-sm outline-none focus:border-[#00E5A3]" />
              <button data-testid={`thread-send-${tid}`} disabled={busy || !text.trim()} className="h-8 w-8 rounded-full bg-[#00E5A3] text-[#0B0F19] grid place-items-center disabled:opacity-40"><Send className="h-3.5 w-3.5" /></button>
            </form>
          ) : <p className="text-xs text-slate-500">Follow this traveler to join the conversation.</p>}
        </div>
      )}
    </div>
  );
}
